/**
 * 일지 목록
 * ---------------------------------------------------------------------------
 * 일지 유형은 주소에서 온다. 예전에는 화면 안의 드롭다운이었는데, 사용자는
 * 한 번에 한 종류만 다루면서도 화면을 열 때마다 유형을 다시 골라야 했다.
 * 이제 메뉴가 유형을 정한다.
 *
 * 기본 조회 기간은 이번 달이다. 일지는 월 단위로 묶어 보는 문서다.
 *
 * 다중 선택 인쇄
 *   한 달치를 하루씩 인쇄하면 서른 번을 눌러야 하고 그 사이에 한 장을
 *   빠뜨린다. 체크한 것을 한 번에 뽑는다.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { App as AntApp, Button, Card, DatePicker, Select, Space, Table, Tag } from 'antd';
import { CheckOutlined, DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import type {
  BulkConfirmResult,
  DailyLogDetail,
  DailyLogSummary,
  LogStatus,
  LogType,
  PageResult,
  Site,
} from '@eco/shared';
import {
  ECO_PERMISSIONS,
  LOG_STATUSES,
  LOG_STATUS_LABELS,
  LOG_TYPES,
  LOG_TYPE_LABELS,
} from '@eco/shared';
import { api, errorMessage } from '../lib/api';
import { downloadCsv } from '../lib/csv';
import { exportColumns, toExportRows } from '../lib/logExport';
import { siteOptions } from '../lib/siteOptions';
import { loadFilter, saveFilter } from '../lib/rememberedFilter';
import { printDailyLogs } from '../lib/print';
import { can, useSession } from '../lib/session';
import { WeatherIcon } from '../components/WeatherIcon';
import { HelpH, HelpList, HelpP, HelpPath, PageHelp } from '../components/PageHelp';
import { PrintDialog } from './daily-log/PrintDialog';

const STATUS_COLOR: Record<LogStatus, string> = {
  SUBMITTED: 'blue',
  CONFIRMED: 'green',
};

/** 서식마다 무엇을 적는 문서인지. 설명 창의 첫 문단이 된다. */
const LOG_TYPE_HELP: Record<LogType, string> =
  {
    T01_AIR:
      '대기배출시설과 방지시설을 하루 몇 시간 돌렸는지, 어떤 원료를 얼마나 썼는지 적는 법정 기록부입니다(별지 제7호서식). 자가측정 성적서는 「자가측정 서류관리」에 올려 두고 인쇄할 때 고릅니다.',
    T02_WATER_CONSIGN:
      '폐수를 위탁 처리하는 사업장이 쓰는 장부입니다(별지 제20호서식). 용수 사용량과 폐수 발생·위탁량을 적습니다.',
    T03_WATER_SELF:
      '폐수를 직접 처리하는 사업장이 쓰는 운영일지입니다(별지 제18호서식). 배출시설·방지시설 가동시간대, 용수와 폐수량, 슬러지, 약품 사용량을 적습니다.',
  };

/** 기억해 두는 조회 조건. 날짜는 Dayjs 가 아니라 문자열로 둔다 — JSON 으로 저장한다. */
interface SavedFilter {
  siteId?: string;
  status?: LogStatus;
  from?: string;
  to?: string;
  page?: number;
}

/** 저장된 기간을 되살린다. 값이 없거나 깨졌으면 이번 달로 돌아간다. */
function toRange(saved: Partial<SavedFilter> | null): [Dayjs, Dayjs] {
  const from = saved?.from ? dayjs(saved.from) : null;
  const to = saved?.to ? dayjs(saved.to) : null;
  if (from?.isValid() && to?.isValid()) return [from, to];
  return [dayjs().startOf('month'), dayjs().endOf('month')];
}

/** 수량은 서식대로 L 이다. 값이 없으면 '-' — 0 과 구분해야 한다. */
const litres = (v: number | null | undefined) =>
  v === null || v === undefined ? '-' : v.toLocaleString('ko-KR', { maximumFractionDigits: 3 });

/**
 * 폐수 위탁 목록 전용 열. 이름은 별지 제20호서식을 따른다.
 *
 * 값은 서버가 목록에 붙여 준 요약(`consign`)에서 읽는다. 행마다 상세를 다시
 * 부르면 50줄에 50번 왕복한다.
 */
const consignColumns: ColumnsType<DailyLogSummary> = [
  {
    title: '가동시간',
    width: 130,
    render: (_, row) => {
      const c = row.consign;
      if (!c?.uptimeStart && !c?.uptimeEnd) return '-';
      return `${c.uptimeStart ?? ''} ~ ${c.uptimeEnd ?? ''}`;
    },
  },
  {
    title: '용수사용량(L)',
    width: 120,
    align: 'right',
    render: (_, row) => litres(row.consign?.waterVolume),
  },
  {
    title: '폐수발생량(L)',
    width: 120,
    align: 'right',
    render: (_, row) => litres(row.consign?.wastewaterGeneration),
  },
  {
    title: '위탁량(L)',
    width: 110,
    align: 'right',
    render: (_, row) => litres(row.consign?.consignmentAmount),
  },
  {
    title: '보관폐수량(L)',
    width: 120,
    align: 'right',
    render: (_, row) => litres(row.consign?.storageWastewater),
  },
  {
    title: '처리업소명',
    width: 160,
    ellipsis: true,
    render: (_, row) => row.consign?.processingCompany ?? '-',
  },
];

export function DailyLogListPage() {
  const navigate = useNavigate();
  const params = useParams();
  const { message } = AntApp.useApp();
  const { data: session } = useSession();
  const queryClient = useQueryClient();

  const write = can(session, ECO_PERMISSIONS.DAILY_LOG_WRITE);
  const canPrint = can(session, ECO_PERMISSIONS.REPORT_EXPORT);
  const canReadDocs = can(session, ECO_PERMISSIONS.SELF_MEASURE_READ);
  const canConfirm = can(session, ECO_PERMISSIONS.DAILY_LOG_CONFIRM);

  // 주소에 이상한 값이 들어와도 화면이 깨지면 안 된다.
  const logType = (LOG_TYPES as readonly string[]).includes(params.logType ?? '')
    ? (params.logType as LogType)
    : 'T01_AIR';

  // 조회 조건을 유형별로 기억한다. 상세에서 고치고 돌아왔을 때 보던 화면이
  // 그대로여야 한다. 유형마다 따로 두는 이유는, 대기에서 보던 사업장과 폐수에서
  // 보던 사업장이 다를 수 있기 때문이다.
  const saved = loadFilter<SavedFilter>(logType);

  const [siteId, setSiteId] = useState<string | undefined>(saved?.siteId);
  const [status, setStatus] = useState<LogStatus | undefined>(saved?.status);
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => toRange(saved));
  const [page, setPage] = useState(saved?.page ?? 1);
  const [selected, setSelected] = useState<string[]>([]);
  const [printing, setPrinting] = useState(false);
  const [exporting, setExporting] = useState(false);
  // 인쇄 대상. 목록의 다중 선택과 행의 단일 인쇄가 같은 대화상자를 쓴다.
  const [printTarget, setPrintTarget] = useState<string[] | null>(null);

  // 유형이 바뀌면 이전 선택은 의미가 없다. 남겨 두면 대기 일지를 고른 채
  // 폐수 목록에서 인쇄를 누르게 된다.
  //
  // 같은 화면 부품이 유형만 바꿔 다시 쓰이므로(주소 파라미터), 여기서 그 유형의
  // 기억을 다시 꺼내 채운다. 첫 마운트에서는 위 useState 가 이미 같은 값을
  // 넣었으므로 바뀌는 것이 없다.
  useEffect(() => {
    const next = loadFilter<SavedFilter>(logType);
    setSiteId(next?.siteId);
    setStatus(next?.status);
    setRange(toRange(next));
    setPage(next?.page ?? 1);
    setSelected([]);
  }, [logType]);

  // 조건이 바뀔 때마다 적어 둔다. 떠날 때 저장하면 뒤로가기나 창 닫기에서 놓친다.
  useEffect(() => {
    saveFilter(logType, {
      siteId,
      status,
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].format('YYYY-MM-DD'),
      page,
    } satisfies SavedFilter);
  }, [logType, siteId, status, range, page]);

  const sites = useQuery<Site[]>({
    queryKey: ['sites'],
    queryFn: async () => (await api.get<Site[]>('/sites')).data,
  });

  const logs = useQuery<PageResult<DailyLogSummary>>({
    queryKey: [
      'daily-logs',
      logType,
      siteId,
      status,
      range[0].format('YYYY-MM-DD'),
      range[1].format('YYYY-MM-DD'),
      page,
    ],
    queryFn: async () =>
      (
        await api.get<PageResult<DailyLogSummary>>('/daily-logs', {
          params: {
            siteId,
            logType,
            status,
            from: range[0].format('YYYY-MM-DD'),
            to: range[1].format('YYYY-MM-DD'),
            page,
            pageSize: 50,
          },
        })
      ).data,
  });

  /**
   * 지금 조건에 걸린 일지를 **전부** 모은다. 엑셀 내려받기가 쓴다.
   *
   * 목록 질의는 화면용이라 50건씩 끊어 온다. 내려받기는 그 끊김을 그대로
   * 따라가면 안 된다 — 한 해치를 받으려는 사람이 첫 50건만 받고 끝낸다.
   * 서버 상한이 한 번에 200건이라 그 단위로 나눠 모은다.
   */
  const fetchAllLogs = async (): Promise<DailyLogSummary[]> => {
    const PAGE = 200;
    const out: DailyLogSummary[] = [];

    for (let p = 1; ; p += 1) {
      const { data } = await api.get<PageResult<DailyLogSummary>>('/daily-logs', {
        params: {
          siteId,
          logType,
          status,
          from: range[0].format('YYYY-MM-DD'),
          to: range[1].format('YYYY-MM-DD'),
          page: p,
          pageSize: PAGE,
        },
      });

      out.push(...data.items);

      // 총건수를 채웠거나 더 돌려줄 것이 없으면 멈춘다. 서버가 total 을
      // 0 으로 주는 일이 있어도 빈 페이지에서 끝나도록 둘 다 본다.
      if (out.length >= data.total || data.items.length === 0) break;
    }

    return out;
  };

  /**
   * 측정값까지 받으려면 건별 상세가 필요하다.
   *
   * 목록 응답에는 표지 값만 있다(배출구별 가동시간, 수질분석 같은 **여러
   * 줄짜리** 자료는 상세에만 있다). 그래서 건마다 한 번씩 더 부른다.
   *
   * 한꺼번에 다 던지지 않는다 — 한 해치라면 수백 건이고, 브라우저와 서버
   * 양쪽에 동시 연결이 몰린다. 여섯 개씩 끊어 보내면 느려지지 않으면서도
   * 줄이 길어져도 안전하다.
   */
  const fetchDetails = async (ids: string[]): Promise<DailyLogDetail[]> => {
    const LANES = 6;
    const out: DailyLogDetail[] = [];

    for (let i = 0; i < ids.length; i += LANES) {
      const slice = ids.slice(i, i + LANES);
      const got = await Promise.all(
        slice.map((id) => api.get<DailyLogDetail>(`/daily-logs/${id}`).then((r) => r.data)),
      );
      out.push(...got);
    }

    // 상세는 병렬로 와서 순서가 섞인다. 날짜순으로 되돌린다 — 엑셀에서
    // 날짜가 뒤섞여 있으면 그것부터 정렬하게 된다.
    return out.sort((a, b) => a.logDate.localeCompare(b.logDate));
  };

  /**
   * 고른 일지들을 한 번에 확인한다.
   *
   * 한 건이 막혀도 나머지는 처리된다 — 확인자로 지정되지 않은 일지나 이미
   * 확인완료된 일지가 섞여 있는 것이 보통이다. 서버가 건별 결과를 주므로
   * 그대로 알린다.
   */
  const confirmMany = useMutation({
    mutationFn: async (ids: string[]) =>
      (await api.post<BulkConfirmResult>('/daily-logs/confirm', { dailyLogIds: ids })).data,
    onSuccess: async (result) => {
      if (result.confirmed.length > 0) {
        message.success(`${result.confirmed.length}건을 확인했습니다.`);
      }
      if (result.skipped.length > 0) {
        message.warning(
          `${result.skipped.length}건은 처리하지 못했습니다: ${result.skipped[0]!.reason}`,
        );
      }
      setSelected([]);
      await queryClient.invalidateQueries({ queryKey: ['daily-logs'] });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  const print = async (selfMeasurementId: string | null, ids = printTarget ?? []) => {
    setPrinting(true);
    const failure = await printDailyLogs({ dailyLogIds: ids, selfMeasurementId });
    setPrinting(false);
    if (failure) message.error(failure);
    else setPrintTarget(null);
  };

  /**
   * 인쇄를 시작한다.
   *
   * 자가측정 성적서를 고르는 대화상자는 대기 운영기록부에만 띄운다. 「3. 자가측정
   * 사항」 은 별지 제7호서식에만 있는 칸이고, 다른 서식에서 성적서를 고르라고
   * 물으면 쓸 데 없는 선택을 한 번 더 하게 된다. 그 외에는 바로 인쇄한다 —
   * 클릭한 그 자리에서 창을 열어야 팝업 차단에 걸리지 않는다.
   */
  const startPrint = (ids: string[]) => {
    if (logType === 'T01_AIR') setPrintTarget(ids);
    else void print(null, ids);
  };

  /**
   * 고른 일지가 모두 한 사업장인가.
   *
   * 한 사업장일 때만 성적서를 직접 고를 수 있다. 섞여 있는데 한 건을 골라
   * 전부에 붙이면 남의 사업장 서류가 실린다.
   */
  const targetSiteId = (() => {
    if (!printTarget) return null;
    const rows = (logs.data?.items ?? []).filter((r) => printTarget.includes(r.dailyLogId));
    const sites = new Set(rows.map((r) => r.siteId));
    return sites.size === 1 ? [...sites][0]! : null;
  })();

  return (
    <Card
      title={LOG_TYPE_LABELS[logType]}
      extra={
        <Space wrap>
          <PageHelp title={`${LOG_TYPE_LABELS[logType]} — 화면 설명`} withLabel>
            <HelpP>{LOG_TYPE_HELP[logType]}</HelpP>

            <HelpH>일지에 시설이 나오지 않는다면</HelpH>
            <HelpP>
              표에 들어갈 시설은 <HelpPath>사업장·시설 → 사업장 시설관리</HelpPath> 에
              등록한 것으로 채워집니다. 거기서 먼저 등록하세요.
            </HelpP>

            <HelpH>상태</HelpH>
            <HelpList
              items={[
                { term: '작성중', desc: '아직 제출하지 않은 상태입니다. 자유롭게 고칠 수 있습니다.' },
                { term: '제출', desc: '확인자에게 넘어간 상태입니다.' },
                {
                  term: '확인',
                  desc: '확인자가 확인한 상태입니다. 수정하려면 먼저 「확인 해제」를 해야 합니다.',
                },
              ]}
            />

            <HelpH>한 번에 여러 건 처리하기</HelpH>
            <HelpP>
              왼쪽 체크상자로 여러 건을 고르면 <b>확인</b>과 <b>인쇄</b>를 한 번에 할 수
              있습니다. 한 달치를 하루씩 여는 대신 쓰세요.
            </HelpP>

            <HelpH>엑셀 내려받기</HelpH>
            <HelpP>
              지금 걸어 둔 조건(사업장·상태·기간)에 해당하는 <b>전체</b>를 받습니다 —
              보고 있는 페이지만이 아닙니다. 단추에 적힌 건수가 받게 될 건수입니다.
              건수가 많으면 모으는 동안 잠깐 기다립니다.
            </HelpP>
            <HelpP>
              엑셀에서 바로 열리는 CSV 로 내려갑니다. 한글이 깨지지 않도록 처리되어
              있고, 사업장명이나 비고에 쉼표가 들어 있어도 칸이 밀리지 않습니다.
            </HelpP>

            <HelpH>조회 조건은 기억됩니다</HelpH>
            <HelpP>
              사업장·상태·기간은 이 탭을 쓰는 동안 유지됩니다. 상세 화면에서 고치고
              돌아와도 보던 조건 그대로입니다.
            </HelpP>
          </PageHelp>

          {/*
            여러 일지를 한 번에 확인한다. 한 달치를 하루씩 열어 확인하면
            서른 번을 눌러야 하고, 확인자는 그 일을 매달 한다.
          */}
          {canConfirm && (
            <Button
              icon={<CheckOutlined />}
              disabled={selected.length === 0}
              loading={confirmMany.isPending}
              onClick={() => confirmMany.mutate(selected)}
            >
              확인자 확인하기{selected.length > 0 ? ` (${selected.length})` : ''}
            </Button>
          )}
          {canPrint && (
            <Button
              icon={<PrinterOutlined />}
              disabled={selected.length === 0}
              loading={printing}
              onClick={() => startPrint(selected)}
            >
              선택 인쇄{selected.length > 0 ? ` (${selected.length})` : ''}
            </Button>
          )}
          {/*
            운영일지는 **법정 기록**이다. 서비스를 그만두더라도 고객이
            가져갈 수 있어야 한다(클라우드컴퓨팅법 제27조의 이용자 정보 반환).
            인쇄는 서식 한 장씩이라 한 해치를 옮기는 데 쓸 수 없다.

            예전에는 **보고 있는 한 페이지(50건)만** 받았다. 단추에 건수를
            적어 두긴 했지만, 「총 120건」 옆에서 「(50건)」을 보고 전체를
            받았다고 넘기기 쉽다. 반환 의무를 한 페이지로 때울 수도 없다.
            이제 조건에 걸린 **전체**를 받는다 — 서버 상한이 한 번에 200건이라
            나눠 불러 모은다.

            표지 값만 담던 것도 바꿨다. 이제 **측정 한 줄이 엑셀 한 행**이다
            (lib/logExport.ts 주석 참고). 그래서 목록만으로는 모자라고 건별
            상세를 한 번씩 더 불러온다.
          */}
          <Button
            icon={<DownloadOutlined />}
            loading={exporting}
            disabled={(logs.data?.total ?? 0) === 0}
            onClick={async () => {
              setExporting(true);
              try {
                const summaries = await fetchAllLogs();
                const details = await fetchDetails(summaries.map((s) => s.dailyLogId));
                downloadCsv(
                  LOG_TYPE_LABELS[logType],
                  toExportRows(details, logType),
                  exportColumns(logType),
                );
              } catch (err) {
                message.error(errorMessage(err));
              } finally {
                setExporting(false);
              }
            }}
          >
            엑셀 내려받기{logs.data?.total ? ` (${logs.data.total}건)` : ''}
          </Button>

          {write && (
            <Button type="primary" onClick={() => navigate(`/logs/${logType}/new`)}>
              일지 작성
            </Button>
          )}
        </Space>
      }
    >
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder="사업장 전체"
          style={{ width: 220 }}
          value={siteId}
          onChange={(v) => {
            setSiteId(v);
            setPage(1);
          }}
          options={siteOptions(sites.data)}
        />
        <Select
          allowClear
          placeholder="상태 전체"
          style={{ width: 140 }}
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          options={LOG_STATUSES.map((s) => ({ value: s, label: LOG_STATUS_LABELS[s] }))}
        />
        <DatePicker.RangePicker
          value={range}
          allowClear={false}
          onChange={(v) => {
            if (v?.[0] && v[1]) {
              setRange([v[0], v[1]]);
              setPage(1);
            }
          }}
        />
      </Space>

      <Table<DailyLogSummary>
        rowKey="dailyLogId"
        // 폐수 위탁은 열이 여섯 개 더 붙는다. 좁은 화면에서는 가로로 밀어 본다.
        scroll={{ x: logType === 'T02_WATER_CONSIGN' ? 1700 : 1000 }}
        loading={logs.isLoading}
        dataSource={logs.data?.items ?? []}
        rowSelection={
          // 인쇄든 확인이든 다중 선택이 필요하다. 둘 다 없으면 체크박스를
          // 둘 이유가 없다.
          canPrint || canConfirm
            ? {
                selectedRowKeys: selected,
                onChange: (keys) => setSelected(keys as string[]),
              }
            : undefined
        }
        onRow={(row) => ({
          style: { cursor: 'pointer' },
          onClick: () => navigate(`/logs/${logType}/${row.dailyLogId}`),
        })}
        pagination={{
          current: page,
          pageSize: logs.data?.pageSize ?? 50,
          total: logs.data?.total ?? 0,
          showSizeChanger: false,
          onChange: setPage,
        }}
        columns={[
          { title: '작성일자', dataIndex: 'logDate', width: 120, fixed: 'left' },
          { title: '사업장', dataIndex: 'siteName', width: 180 },
          {
            title: '날씨',
            dataIndex: 'weather',
            width: 110,
            render: (_, row) => <WeatherIcon code={row.weatherCode} label={row.weather} showLabel />,
          },
          {
            title: '기온',
            dataIndex: 'temperature',
            width: 80,
            render: (v: number | null) => (v === null ? '-' : `${v}℃`),
          },
          // 폐수 위탁은 서식의 주요 칸을 목록에서 바로 본다. 한 달치를 훑으며
          // 위탁량이 비어 있는 날, 보관량이 쌓여 가는 추세를 찾는 화면이다.
          ...(logType === 'T02_WATER_CONSIGN' ? consignColumns : []),
          { title: '작성자', dataIndex: 'writerName', width: 110, render: (v) => v ?? '-' },
          { title: '확인자', dataIndex: 'confirmerName', width: 110, render: (v) => v ?? '-' },
          {
            title: '상태',
            dataIndex: 'status',
            width: 110,
            render: (s: LogStatus) => <Tag color={STATUS_COLOR[s]}>{LOG_STATUS_LABELS[s]}</Tag>,
          },
          {
            title: '',
            width: 70,
            render: (_, row) =>
              canPrint && (
                <Button
                  size="small"
                  type="link"
                  icon={<PrinterOutlined />}
                  // 행을 누르면 편집기로 가므로 인쇄 클릭이 전파되면 안 된다.
                  onClick={(e) => {
                    e.stopPropagation();
                    startPrint([row.dailyLogId]);
                  }}
                >
                  인쇄
                </Button>
              ),
          },
        ]}
      />

      <PrintDialog
        open={printTarget !== null}
        count={printTarget?.length ?? 0}
        siteId={targetSiteId}
        canChoose={canReadDocs}
        loading={printing}
        onCancel={() => setPrintTarget(null)}
        onConfirm={(id) => void print(id)}
      />
    </Card>
  );
}
